<template>
  <Teleport to="body">
    <Transition name="assistant-slide">
      <aside
        v-if="store.open"
        class="assistant"
        role="complementary"
        :aria-label="t('assistant.title')"
        :style="panelStyle"
      >
        <!-- Drag the edge: 420 px is right for a question and wrong for a table
             of results, and only the person reading it knows which they have.
             The width is remembered per browser. Arrow keys work too, so it is
             not mouse-only. -->
        <div
          class="grip"
          :class="{ 'grip--dragging': dragging }"
          role="separator"
          aria-orientation="vertical"
          :aria-label="t('assistant.widen')"
          :aria-valuenow="width"
          tabindex="0"
          @pointerdown="startResize"
          @dblclick="resetWidth"
          @keydown.left.prevent="resizeBy(40)"
          @keydown.right.prevent="resizeBy(-40)"
        >
          <span class="grip__bar" aria-hidden="true" />
        </div>

        <!--
          ── La cabecera ──────────────────────────────────────────────────
          Iconos dibujados y no caracteres sueltos (✆ € ＋ ☰): cada fuente
          los pintaba a un tamaño y una altura distintos, y en un teléfono
          eran cuatro dianas de 28 px pegadas. Aquí cada botón es una diana
          de 40 px (44 con el dedo) con su nombre para el lector de pantalla.

          En el teléfono el panel ES la pantalla, así que se cierra como una
          pantalla de app: con la flecha de la izquierda. En el ordenador es
          un panel lateral y se cierra con la X de la derecha.
        -->
        <header class="assistant__head">
          <button
            v-if="phone"
            type="button"
            class="head-btn head-btn--back"
            :aria-label="t('assistant.close')"
            @click="store.toggle()"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          </button>

          <div class="assistant__heading">
            <span class="assistant__title">{{ headingTitle }}</span>
          </div>

          <!-- Hablarle sin manos. Solo donde la instancia oye: sin oído, una
               llamada es un teléfono que no contesta. -->
          <button
            v-if="store.canListen"
            type="button"
            class="head-btn head-btn--call"
            :title="t('assistant.call.startHint')"
            :aria-label="t('assistant.call.start')"
            @click="store.calling = true"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" /></svg>
            <span class="head-btn__label">{{ t('assistant.call.start') }}</span>
          </button>

          <button
            v-if="canSeeCosts"
            type="button"
            class="head-btn"
            :class="{ 'head-btn--on': showCosts }"
            :title="t('assistant.costs.title')"
            :aria-label="t('assistant.costs.title')"
            :aria-pressed="showCosts"
            @click="showCosts = !showCosts; showHistory = false; showMemories = false"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 7a6 6 0 1 0 0 10M4 10h9M4 14h9" /></svg>
          </button>
          <button
            type="button"
            class="head-btn"
            :class="{ 'head-btn--on': showHistory }"
            :title="t('assistant.history')"
            :aria-label="t('assistant.history')"
            :aria-pressed="showHistory"
            @click="showHistory = !showHistory; showCosts = false; showMemories = false"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8" /><path d="M3 3v5h5M12 7v5l3 2" /></svg>
          </button>
          <!-- Lo que recuerda de esta persona (AssistantMemories). -->
          <button
            type="button"
            class="head-btn"
            :class="{ 'head-btn--on': showMemories }"
            :title="t('assistant.memory.title')"
            :aria-label="t('assistant.memory.title')"
            :aria-pressed="showMemories"
            @click="showMemories = !showMemories; showHistory = false; showCosts = false"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
          </button>
          <button
            type="button"
            class="head-btn"
            :title="t('assistant.new')"
            :aria-label="t('assistant.new')"
            @click="startNew"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>
          </button>
          <button
            v-if="!phone"
            type="button"
            class="head-btn"
            :aria-label="t('assistant.close')"
            :title="t('assistant.close')"
            @click="store.toggle()"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12" /></svg>
          </button>
        </header>

        <!-- ── History ────────────────────────────────────────────────────── -->
        <AssistantCosts v-if="showCosts" class="assistant__history" />

        <AssistantMemories v-else-if="showMemories" class="assistant__history" />

        <div v-else-if="showHistory" class="assistant__history">
          <p v-if="!store.conversations.length" class="assistant__hint pad">{{ t('assistant.noHistory') }}</p>

          <div
            v-for="conversation in store.conversations"
            :key="conversation._id"
            class="history-item"
            :class="{ 'history-item--current': conversation._id === store.conversationId }"
          >
            <button type="button" class="history-item__open" @click="open(conversation._id)">
              <span class="truncate">{{ conversationTitle(conversation, { t, te }) }}</span>
              <span class="text-xs text-mute">{{ formatRelative(conversation.updatedAt) }}</span>
            </button>
            <UiClose
              size="sm"
              danger
              class="history-item__delete"
              :label="t('assistant.deleteConversation')"
              @click="remove(conversation)"
            />
          </div>

          <!-- Todo el historial de una vez: lo propio y solo lo propio (el
               servidor filtra por quien lo pide). Al final y discreto, porque
               no tiene vuelta. -->
          <div v-if="store.conversations.length" class="history-clear">
            <UiButton size="sm" variant="ghost" @click="removeAll">
              {{ t('assistant.deleteAll') }}
            </UiButton>
          </div>
        </div>

        <!-- ── Conversation ───────────────────────────────────────────────── -->
        <div v-else ref="thread" class="assistant__thread" @click="followLink" @scroll.passive="trackBottom">
          <!-- La primera pantalla: empezar a trabajar, no leer instrucciones. -->
          <AssistantHome v-if="!store.messages.length" />

          <template v-for="(message, index) in store.messages" :key="index">
            <div v-if="message.rejected" class="note">{{ t('assistant.rejected') }}</div>

            <div v-else class="bubble" :class="`bubble--${message.role}`">
              <!-- Answers arrive in Markdown, because that is what a language
                   model writes whether asked to or not. -->
              <UiMarkdown v-if="message.role === 'assistant' && message.text" :source="message.text" :is-trusted="followable(index)" />
              <p v-if="message.reply" class="bubble__text">{{ phrase(message.reply) }}</p>
              <!-- Detenida mientras se escribía: lo leído se queda, y se dice
                   que no es la respuesta entera. -->
              <p v-if="message.stopped" class="bubble__stopped">{{ t('assistant.stoppedAnswer') }}</p>

              <!-- Lo que de verdad pasó, del módulo y no del modelo: aunque la
                   frase de arriba dijera otra cosa, esto es lo que se guardó. -->
              <AssistantOutcome v-if="message.outcome" :outcome="message.outcome" />
              <template v-if="message.role !== 'assistant'">
                <!-- The note that was sent, and under it what was heard from
                     it. Both, on purpose: the transcript is the half that can
                     be wrong, and nobody can check it against a recording they
                     cannot play back. -->
                <UiAudio
                  v-if="message.spoken"
                  class="bubble__note"
                  :src="message.spoken.url"
                  :label="t('assistant.voiceNote')"
                />
                <!-- Lo que se adjuntó con la pregunta (attachments.js). -->
                <ul v-if="message.attachments?.length" class="bubble__files">
                  <li v-for="(file, at) in message.attachments" :key="at" class="bubble__file">
                    <img v-if="thumbOf(file)" :src="thumbOf(file)" alt="" />
                    <span v-else aria-hidden="true">{{ file.kind === 'email' ? '✉' : '📄' }}</span>
                    <span class="bubble__file-name">{{ file.name }}</span>
                  </li>
                </ul>
                <p class="bubble__text">{{ message.text }}</p>
              </template>

              <!-- Where the answer came from. Folded away by default: it is
                   reassurance, not content. -->
              <details v-if="message.steps?.length" class="trail">
                <summary>{{ t('assistant.steps', message.steps.length) }}</summary>
                <ul>
                  <li v-for="(step, at) in message.steps" :key="at" :class="{ 'trail--failed': !step.ok }">
                    <span aria-hidden="true">{{ step.ok ? '✓' : '×' }}</span>
                    {{ stepLabel(step) }}
                  </li>
                </ul>
              </details>

              <div v-if="message.role === 'assistant' && message.text" class="bubble__tools">
                <button
                  v-if="speech.supported"
                  type="button"
                  class="bubble__tool"
                  :title="reading === index ? t('assistant.stopReading') : t('assistant.readAloud')"
                  :aria-pressed="reading === index"
                  @click="toggleReading(message.text, index)"
                >{{ reading === index ? '◼' : '▶' }}</button>
                <button
                  type="button"
                  class="bubble__tool"
                  :title="t('assistant.copyAnswer')"
                  @click="copy(message.text)"
                >⧉</button>
              </div>
            </div>
          </template>

          <!-- ── La respuesta mientras se escribe ─────────────────────────── -->
          <!-- Como una burbuja más; el `done` la sustituye por la comprobada.
               Sin enlaces que seguir: aún no ha pasado por el servidor entera. -->
          <div v-if="store.sending && store.draft" class="bubble bubble--assistant bubble--draft" aria-live="polite">
            <UiMarkdown :source="store.draft" :is-trusted="false" />
          </div>

          <!-- ── What it is doing right now ───────────────────────────────── -->
          <div v-if="store.sending" class="working">
            <span class="working__dots"><i /><i /><i /></span>
            <span class="working__text">
              {{ store.activity?.type === 'tool' ? stepLabel(store.activity) : t('assistant.thinking') }}
            </span>
          </div>
          <ul v-if="store.sending && store.liveSteps.length" class="live-steps">
            <li v-for="(step, at) in store.liveSteps" :key="at">
              <span aria-hidden="true">{{ step.ok ? '✓' : '×' }}</span> {{ stepLabel(step) }}
            </li>
          </ul>

          <!-- ── A write it wants to make. Nothing has happened yet. ──────── -->
          <!-- Solo completa: mientras falte algo, el asistente lo pregunta
               (en el hilo y en voz alta) y la tarjeta no confunde. -->
          <AssistantProposal
            v-if="store.pendingAction && store.pendingAction.complete !== false"
            :action="store.pendingAction"
            :hint="store.canListen ? t('assistant.confirmHintVoice') : ''"
            @confirm="item => store.confirmAction(item)"
            @reject="store.rejectAction()"
          />

          <!-- ── What it still needs, as a widget ───────────────────────── -->
          <AssistantAsk v-if="store.widget && !store.sending" :key="store.widget.id" :widget="store.widget" />

          <div v-if="store.listening" class="working">
            <span class="working__dots"><i /><i /><i /></span>
            <span class="working__text">{{ t('assistant.listening') }}</span>
          </div>

          <!-- Not an error: a thumb over the microphone is the person's own
               mistake, and it deserves a hint rather than a red banner. -->
          <p v-if="store.heardNothing" class="note">{{ t('assistant.heardNothing') }}</p>

          <p v-if="speech.blocked.value" class="note">{{ t('assistant.speechBlocked') }}</p>

          <div v-if="store.error" class="assistant__error" role="alert">
            <p>{{ errorMessage(store.error, t) }}</p>
            <UiButton size="sm" variant="secondary" @click="store.retry()">
              {{ t('assistant.retry') }}
            </UiButton>
          </div>
        </div>

        <!-- Sin red: lo escrito se queda en la caja hasta que vuelva. -->
        <p v-if="!online" class="note note--offline" role="status">{{ t('assistant.ask.offline') }}</p>

        <!-- ── Composer ───────────────────────────────────────────────────── -->
        <!-- Solo en la conversación: en el historial o los costes no hay a
             quién escribirle, y la caja ocupaba sitio como en una pantalla aparte. -->
        <AssistantAttach v-if="!showHistory && !showCosts && !showMemories" />
        <form v-if="!showHistory && !showCosts && !showMemories" class="composer" @submit.prevent="submit">
          <!-- Adjuntar una foto, un PDF: abre la bandeja de encima. -->
          <button
            type="button"
            class="composer__clip"
            :class="{ 'composer__clip--on': store.attachOpen || store.attachments.length }"
            :title="t('assistant.attach.button')"
            :aria-label="t('assistant.attach.button')"
            :aria-expanded="store.attachOpen"
            @click="store.attachOpen = !store.attachOpen"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 11.5l-8.6 8.6a5 5 0 0 1-7.1-7.1l8.6-8.6a3.3 3.3 0 0 1 4.7 4.7l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l8-8" /></svg>
          </button>
          <!-- Press once to record, again to send. There is no third press:
               a voice note that has to be reviewed before sending is a form,
               and this is a conversation. -->
          <!-- Pressing the microphone shuts the assistant up first. Otherwise
               the recording opens while the answer is still being read out, and
               the first thing it hears is the ERP's own voice. -->
          <UiAudioRecorder
            v-if="store.canListen"
            v-model="recording"
            class="composer__mic"
            @click.capture="speech.stop()"
            filename="question"
            :max-seconds="120"
            :label="t('assistant.voiceNote')"
            :disabled="!store.canSpeak"
          />
          <textarea
            ref="input"
            v-model="draft"
            class="composer__input"
            rows="1"
            :placeholder="composerPlaceholder"
            :enterkeyhint="touch ? 'enter' : 'send'"
            @keydown.enter.exact="onEnter"
            @input="autoGrow"
          />
          <!-- Stop replaces send while it is working: a question going nowhere
               should be stoppable without reloading the page. -->
          <button
            v-if="store.sending"
            type="button"
            class="composer__send composer__send--stop"
            :title="t('assistant.stop')"
            :aria-label="t('assistant.stop')"
            @click="store.stop()"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="7" width="10" height="10" rx="1.5" /></svg>
          </button>
          <!-- mousedown.prevent: pulsar enviar no le quita el foco a la caja, y
               en el teléfono el teclado se queda abierto como en cualquier chat. -->
          <button
            v-else
            type="submit"
            class="composer__send"
            :disabled="!draft.trim() || !store.canAsk"
            :title="t('assistant.shortcut')"
            :aria-label="t('assistant.shortcut')"
            @mousedown.prevent
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7" /></svg>
          </button>
        </form>
      </aside>
    </Transition>

    <AssistantCall v-if="store.calling" @end="endCall" />
  </Teleport>
</template>

<script setup>
import { ref, computed, nextTick, watch, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
  UiButton, UiMarkdown, UiClose, UiAudio, UiAudioRecorder, api,
  useToast, useConfirm, useSpeech, speakable, errorMessage, formatRelative, onViewportChange,
} from '@general/ui-kit'

import { useAssistantStore } from '../store'
import { collectContext } from '../context'
import { toolLabel } from '../toolLabel'
import { conversationTitle } from '../conversationTitle'
import AssistantCall from './AssistantCall.vue'
import AssistantHome from './AssistantHome.vue'
import AssistantProposal from './AssistantProposal.vue'
import AssistantOutcome from './AssistantOutcome.vue'
import AssistantAsk from './AssistantAsk.vue'
import AssistantCosts from './AssistantCosts.vue'
import AssistantMemories from './AssistantMemories.vue'
import AssistantAttach from './AssistantAttach.vue'
import { useAuthStore } from '@general/auth/frontend'

const router = useRouter()
const { t, te } = useI18n()
const store = useAssistantStore()

const toast = useToast()
const { confirm } = useConfirm()

const draft = ref('')
const thread = ref(null)
const input = ref(null)
const showHistory = ref(false)

// ── Hoy ──────────────────────────────────────────────────────────────────────
// A question the «Today» card prepared («Replanificar»): into the box, unsent.
watch(() => store.prepared, async (text) => {
  if (!text) return
  draft.value = text
  store.prepared = null
  await nextTick()
  input.value?.focus()
})

// The morning notification opens `/?assistant=today`: the panel on its first
// screen, and the address cleaned so a reload does not open it again.
const route = useRoute()
watch(() => route.query.assistant, (wanted) => {
  if (wanted !== 'today') return
  store.openToday()
  const rest = { ...route.query }
  delete rest.assistant
  router.replace({ query: rest })
}, { immediate: true })

// ── Lo que cuesta ────────────────────────────────────────────────────────────
const auth = useAuthStore()
const canSeeCosts = computed(() => auth.can('ops:read'))
const showCosts = ref(false)
// «Lo que recuerdo de ti»: una vista más, como el historial y los costes.
const showMemories = ref(false)

/** La cabecera dice dónde se está: la conversación, el historial, los costes o la memoria. */
const headingTitle = computed(() => {
  if (showHistory.value) return t('assistant.history')
  if (showCosts.value) return t('assistant.costsHeading')
  if (showMemories.value) return t('assistant.memory.title')
  return t('assistant.title')
})

/** What a tool is doing, in the reader's language (../toolLabel.js). */
const stepLabel = (step) => toolLabel(step, { t, te })

/** Una frase que escribieron las reglas ({ text, key, params }), en el idioma de quien lee. */
function phrase(said) {
  if (!said) return ''
  if (typeof said === 'string') return said
  return said.key && te(said.key) ? t(said.key, said.params ?? {}) : (said.text ?? '')
}

// ── La llamada ─────────────────────────────────────────────────────────────
/**
 * Al colgar no hay nada que recargar: la llamada escribía en este mismo store,
 * así que el hilo, el borrador y el widget siguen aquí tal cual. Seguir por
 * escrito es solo enfocar la caja.
 */
async function endCall({ toText = false } = {}) {
  store.calling = false
  store.open = true
  store.loadConversations()
  if (toText) { await nextTick(); input.value?.focus() }
}

// ── Voice ────────────────────────────────────────────────────────────────
//
// The recorder's model, held only long enough to send it. A voice note in a
// conversation is not a file somebody is composing: it leaves on the same press
// that stops the recording.
const recording = ref(null)
const speech = useSpeech()
/** Which message is being read out, by index. Null when nothing is. */
const reading = ref(null)

watch(recording, async (file) => {
  if (!file) return
  recording.value = null      // cleared first: the recorder must never show a
  await store.sendVoice(file) // "recorded, send it?" state on its way out
})

/** What the answer sounds like: the figures, without the table around them. */
const forSpeech = (markdown) => speakable(markdown, {
  phrases: {
    table: (rows) => t('assistant.speech.table', rows),
    code: () => t('assistant.speech.code'),
    more: () => t('assistant.speech.more'),
  },
}).text

const locale = () => document.documentElement.lang || 'es'

function toggleReading(markdown, index) {
  if (reading.value === index) { speech.stop(); reading.value = null; return }
  reading.value = index
  speech.speak(forSpeech(markdown), { locale: locale() })
}

// The button goes back to ▶ on its own when the voice reaches the end.
watch(speech.speaking, (talking) => { if (!talking) reading.value = null })

// An answer to a spoken question is read back without being asked twice — and
// an answer to a typed one never is. Somebody who typed did not ask to be
// spoken to, least of all in an office.
//
// When the turn ends holding a proposal, what gets read is the MODEL's own
// sentence and not the confirmation card's. The card shows what the module
// wrote, and every module writes it in English («Filing a meter reading»); the
// model writes in the language it was asked in. Read aloud, only one of those
// is any use — and the model's is also the part that says what it understood,
// which is the thing being agreed to.
watch(() => store.sending, (busy, wasBusy) => {
  if (busy || !wasBusy || !store.spokeLast || !speech.supported) return

  const index = store.messages.length - 1
  const last = store.messages[index]
  const waiting = Boolean(store.pendingAction)

  // Nothing said in the person's own language. Rather than read out a sentence
  // written for a screen in another one, send them to the screen.
  if (last?.role !== 'assistant' || !last.text) {
    if (waiting) speech.speak(t('assistant.proposalOnScreen'), { locale: locale() })
    return
  }

  const said = forSpeech(last.text)
  speech.speak(waiting ? `${said} ${t('assistant.confirmAloud')}` : said, { locale: locale() })
  reading.value = index
})

// ── Width ────────────────────────────────────────────────────────────────────
const WIDTH_KEY = 'general.assistantWidth'
const DEFAULT_WIDTH = 420
const MIN_WIDTH = 320
// Room left for the page behind it, so the panel can never swallow the window
// whole and strand the user with nothing to look at.
const EDGE_MARGIN = 120

const dragging = ref(false)
const width = ref(clampWidth(Number(localStorage.getItem(WIDTH_KEY)) || DEFAULT_WIDTH))

function clampWidth(value) {
  const max = Math.max(MIN_WIDTH, window.innerWidth - EDGE_MARGIN)
  return Math.round(Math.min(Math.max(value, MIN_WIDTH), max))
}

function remember() {
  localStorage.setItem(WIDTH_KEY, String(width.value))
}

function resizeBy(delta) {
  width.value = clampWidth(width.value + delta)
  remember()
}

function resetWidth() {
  width.value = clampWidth(DEFAULT_WIDTH)
  remember()
}

function startResize(event) {
  event.preventDefault()
  dragging.value = true
  // Nothing on the page should select while a drag is in progress.
  document.body.style.userSelect = 'none'

  const onMove = (move) => { width.value = clampWidth(window.innerWidth - move.clientX) }
  const onUp = () => {
    dragging.value = false
    document.body.style.userSelect = ''
    remember()
    window.removeEventListener('pointermove', onMove)
    window.removeEventListener('pointerup', onUp)
  }

  window.addEventListener('pointermove', onMove)
  window.addEventListener('pointerup', onUp)
}

// ── La red y lo que dice la caja ─────────────────────────────────────────────
const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine !== false)
const markOnline = () => { online.value = true }
const markOffline = () => { online.value = false }
window.addEventListener('online', markOnline)
window.addEventListener('offline', markOffline)
onBeforeUnmount(() => {
  window.removeEventListener('online', markOnline)
  window.removeEventListener('offline', markOffline)
})

/**
 * La caja dice qué se puede escribir AHORA: con una tarjeta esperando, que se
 * corrige escribiendo; con un widget, que también vale contestar con palabras.
 */
const composerPlaceholder = computed(() => {
  if (!online.value) return t('assistant.ask.offlinePlaceholder')
  if (store.pendingAction) return t('assistant.ask.correctPlaceholder')
  if (store.widget) return t('assistant.ask.answerPlaceholder')
  return t('assistant.placeholder')
})

// ── Talking ──────────────────────────────────────────────────────────────────
function submit() {
  ask(draft.value)
}

/**
 * Intro envía en el ordenador. En el teléfono es un salto de línea, como en
 * cualquier app de mensajes: el teclado no tiene Mayús+Intro, y un Intro que
 * envía a medio pensar no se puede deshacer. Se envía con la flecha.
 */
function onEnter(event) {
  if (touch) return
  event.preventDefault()
  submit()
}

async function ask(text) {
  const question = text?.trim()
  // Sin red no se manda: se queda escrito, y el aviso de encima lo dice.
  if (!question || !store.canAsk || !online.value) return

  draft.value = ''
  if (input.value) input.value.style.height = 'auto'
  await store.send(question)
}

/** La miniatura de lo adjuntado: la copia del navegador, o el fichero por su puerta. */
function thumbOf(file) {
  if (file.url) return file.url
  if (file.kind === 'file' && file.id && String(file.mimeType ?? '').startsWith('image/')) return api.urlWithToken(`/files/${file.id}`)
  return null
}

function autoGrow(event) {
  const el = event.target
  el.style.height = 'auto'
  // Capped: a long paste must not swallow the conversation above it.
  el.style.height = `${Math.min(el.scrollHeight, 160)}px`
}

function startNew() {
  showHistory.value = false
  showMemories.value = false
  store.newConversation()
}

async function open(id) {
  showHistory.value = false
  await store.openConversation(id)
}

async function remove(conversation) {
  const yes = await confirm({
    title: t('assistant.deleteTitle'),
    message: t('assistant.deleteMessage'),
    confirmLabel: t('common.delete'),
    cancelLabel: t('common.cancel'),
    danger: true,
  })
  if (!yes) return

  await store.remove(conversation._id)
  toast.success(t('assistant.deleted'))
}

async function removeAll() {
  const yes = await confirm({
    title: t('assistant.deleteAllTitle'),
    message: t('assistant.deleteAllMessage'),
    confirmLabel: t('common.delete'),
    cancelLabel: t('common.cancel'),
    danger: true,
  })
  if (!yes) return

  await store.removeAll()
  toast.success(t('assistant.deletedAll'))
}

async function copy(text) {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(t('assistant.copied'))
  } catch {
    // Clipboard access can be refused; the answer is on screen either way.
  }
}

/**
 * Which links in an answer can be followed: our own pages, and the outside
 * addresses the person typed themselves. Everything else stays as text.
 *
 * What the model writes is not the person's: it reads tool results, and a
 * tool result can quote whatever somebody typed into a record — a supplier's
 * note, an email subject. A link to the web that it makes up or copies from
 * there, under a friendly label, is exactly how a phishing page gets a click.
 * A path of ours is safe (the router opens it, behind the same permissions);
 * an address the person wrote is one they already chose to visit.
 *
 * The decision is handed to the shared renderer (`isTrusted`), which applies it
 * to exactly the links it draws, after lifting the code out the way it shows
 * it. Deciding it here on the Markdown, split by every backtick of the whole
 * answer, a lone backtick earlier on made the link after it look like code,
 * and it was left alive (2026-10-21).
 *
 * @param {string[]} written what the person wrote in this conversation
 * @returns {(href: string) => boolean}
 */
function trustedLinks(written) {
  const typed = String((written ?? []).join(' '))
  const urls = new Set((typed.match(/(?:https?:\/\/|mailto:|tel:)[^\s<>()[\]]+/gi) ?? [])
    .map(url => url.replace(/[.,;:!?'"»)]+$/, '')))
  const address = (url) => url.replace(/^(mailto|tel):/i, '')
  return (href) => {
    const url = String(href ?? '').trim()
    // A path of ours, and only that: `//host` and `/\host` start like one and
    // the browser takes both to another site.
    return /^\/(?![/\\])/.test(url)
      || urls.has(url)
      || (/^(mailto|tel):/i.test(url) && address(url).length > 3 && typed.includes(address(url)))
  }
}

/** Which links of the answer at `index` its reader may follow. */
function followable(index) {
  const written = store.messages.slice(0, index).filter(message => message.role === 'user').map(message => message.text)
  return trustedLinks(written)
}

/**
 * A link to a page of this ERP opens that page, not a second copy of the app.
 *
 * The assistant answers "where do I do that?" with a Markdown link to a path of
 * ours, and the shared renderer marks every link `target="_blank"` — right for a
 * link to the web, wrong for one of ours: it would reload the whole SPA into a
 * new tab. Intercepted here rather than fixed in the renderer, which is shared
 * by four packages and has no router to push to.
 *
 * One listener on the thread instead of one per link: the answers are rendered
 * from HTML, so there is no component to bind to.
 */
function followLink(event) {
  // Modified and middle clicks mean "open it somewhere else", and where that is
  // remains the browser's business.
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

  const anchor = event.target?.closest?.('a[href]')
  const href = anchor?.getAttribute('href')
  // Only our own paths. A link to the outside keeps its new tab, and `//host`
  // is the outside too, however much it starts like a path (and so is `/\host`).
  if (!/^\/(?![/\\])/.test(href ?? '')) return

  event.preventDefault()
  router.push(href)
}

// ── Following the conversation ───────────────────────────────────────────────
// Watches the activity too, so the panel keeps the progress line in view while
// it works rather than pinning to the last message and leaving it below.
watch(
  () => [store.messages.length, store.liveSteps.length, store.sending],
  async () => {
    await nextTick()
    if (thread.value) thread.value.scrollTop = thread.value.scrollHeight
  }
)

// En el teléfono no se enfoca al abrir: el teclado saltaría mientras el panel
// aún entra, tapando media pantalla antes de que nadie haya pedido escribir.
watch(() => store.open, async (isOpen) => {
  if (!isOpen) return
  await nextTick()
  if (!touch) input.value?.focus()
})

const onWindowResize = () => { width.value = clampWidth(width.value) }
window.addEventListener('resize', onWindowResize)
onBeforeUnmount(() => window.removeEventListener('resize', onWindowResize))

// ── En el teléfono, una pantalla de chat ─────────────────────────────────────
//
// Con `top: 0; bottom: 0` el panel medía la ventana entera, y al salir el
// teclado ni iOS ni Chrome encogen la ventana: desplazan lo que se ve. La
// cabecera se iba por arriba, la caja subía con un hueco debajo y el hilo
// bailaba. En el teléfono mide lo que queda por encima del teclado
// (--app-height, ui-kit utils/viewport.js; ver el CSS del final), como la
// pantalla de chat de una app: cabecera arriba, caja justo encima del teclado.
const touch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
const phoneQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 640px)') : null
const phone = ref(Boolean(phoneQuery?.matches))
const onPhoneChange = (event) => { phone.value = event.matches }
phoneQuery?.addEventListener?.('change', onPhoneChange)

// El ancho que se arrastra es del ordenador; en el teléfono es la pantalla.
const panelStyle = computed(() => (phone.value ? {} : { width: `${width.value}px` }))

function isAtBottom() {
  const el = thread.value
  return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 80
}

function scrollToEnd() {
  if (thread.value) thread.value.scrollTop = thread.value.scrollHeight
}

// Quien estaba leyendo lo último lo sigue viendo cuando el teclado encoge el hilo.
let lastHeight = 0
let wasAtBottom = true
const stopViewport = onViewportChange((state) => {
  if (state.height !== lastHeight && wasAtBottom) nextTick(scrollToEnd)
  lastHeight = state.height
})
const trackBottom = () => { wasAtBottom = isAtBottom() }

// La respuesta que crece mientras se escribe arrastra el hilo solo a quien ya
// estaba abajo: quien subió a releer algo no pierde lo que leía a cada palabra.
watch(() => store.draft.length, async (length, before) => {
  if (!length || length < before || !wasAtBottom) return
  await nextTick()
  scrollToEnd()
})

onBeforeUnmount(() => {
  stopViewport()
  phoneQuery?.removeEventListener?.('change', onPhoneChange)
})
</script>

<style scoped>
.assistant {
  position: fixed;
  top: 0; right: 0; bottom: 0;
  z-index: 140;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  background: var(--color-surface);
  border-left: 1px solid var(--color-border);
  box-shadow: var(--shadow-lg);
}


/* Wide enough to grab without aiming, with a visible handle so it is obvious
   the panel can be widened at all. */
.grip {
  position: absolute;
  top: 0; left: -5px; bottom: 0;
  width: 11px;
  z-index: 1;
  display: grid;
  place-items: center;
  cursor: col-resize;
  touch-action: none;
}

.grip__bar {
  width: 3px;
  height: 42px;
  border-radius: var(--radius-full);
  background: var(--color-border);
  transition: background var(--fast), height var(--fast);
}
.grip:hover .grip__bar,
.grip:focus-visible .grip__bar,
.grip--dragging .grip__bar {
  background: var(--color-primary);
  height: 80px;
}
.grip:focus-visible { outline: none; }

.assistant__head {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  min-height: 56px;
  padding: var(--space-2) var(--space-2) var(--space-2) var(--space-4);
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}
.assistant__heading { flex: 1; min-width: 0; }
.assistant__title {
  display: block;
  font-weight: 650;
  font-size: var(--text-md, 15px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Un botón de cabecera: icono de trazo, diana de 40 px, sin caja hasta que se
   toca o está activo. */
.head-btn {
  flex: none;
  min-width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-full);
  background: none;
  color: var(--color-text-soft);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: background var(--fast), color var(--fast);
}
.head-btn svg {
  width: 20px; height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.head-btn:hover { background: var(--color-gray-100); color: var(--color-text); }
.head-btn:active { background: var(--color-gray-200, var(--color-gray-100)); }
.head-btn:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 1px; }
.head-btn--on { background: var(--color-primary-soft); color: var(--color-primary); }

/* Llamar es la acción con nombre: la única que se lee además de verse. */
.head-btn--call {
  padding: 0 12px;
  background: var(--color-primary-soft);
  color: var(--color-primary);
}
.head-btn--call:hover { background: var(--color-primary-soft); color: var(--color-primary); filter: brightness(1.08); }
.head-btn--call svg { width: 18px; height: 18px; }

.head-btn--back { margin-left: calc(-1 * var(--space-2)); }
.head-btn--back svg { width: 24px; height: 24px; }

.assistant__hint { font-size: var(--text-sm); color: var(--color-text-mute); }
.pad { padding: var(--space-4); }
.history-clear { display: flex; justify-content: center; padding: var(--space-3) var(--space-2); }

/* ── Thread ───────────────────────────────────────────────────────────────── */
.assistant__thread {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.opening { text-align: center; padding: var(--space-6) var(--space-2); }
.opening__title { font-weight: 650; font-size: var(--text-lg); margin-bottom: var(--space-2); }

.opening__context {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin-top: var(--space-4);
}

.opening__suggestions {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-top: var(--space-6);
  text-align: left;
}

.suggestion {
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  text-align: left;
  font-size: var(--text-sm);
  color: var(--color-text);
  transition: border-color var(--fast), background var(--fast);
}
.suggestion:hover {
  border-color: var(--color-primary);
  background: var(--color-primary-soft);
}

.opening__modules {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  justify-content: center;
  margin-top: var(--space-5);
}
.opening__modules li {
  padding: 3px 10px;
  border-radius: var(--radius-full);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-size: var(--text-xs);
  font-weight: 600;
}

/* ── Bubbles ──────────────────────────────────────────────────────────────── */
.bubble {
  position: relative;
  max-width: 90%;
  padding: var(--space-3);
  border-radius: var(--radius);
  font-size: var(--text-sm);
  line-height: 1.6;
  word-break: break-word;
}
.bubble--user {
  align-self: flex-end;
  background: var(--color-primary);
  color: var(--color-text-on-primary);
  white-space: pre-wrap;
}
/* La respuesta ocupa el ancho entero, como en las apps de chat con un
   asistente: una tabla o una lista de órdenes metida en una burbuja del 90 %
   dejaba media pantalla en blanco. Burbuja estrecha solo para lo que escribe
   la persona. */
.bubble--assistant {
  align-self: stretch;
  max-width: 100%;
  background: var(--color-surface-2);
  padding-right: var(--space-6);
}

/* Mientras se escribe: la misma burbuja, sin herramientas todavía. */
.bubble--draft { padding-right: var(--space-3); }
.bubble__stopped {
  margin: var(--space-1) 0 0;
  font-size: var(--text-xs);
  color: var(--color-text-mute);
}

/* ── What you can do to an answer ───────────────────────────────────── */
.bubble__tools {
  position: absolute;
  top: 4px; right: 4px;
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity var(--fast);
}
.bubble--assistant:hover .bubble__tools,
/* Kept visible while it is talking: the stop button is the one control nobody
   should have to go looking for. */
.bubble__tools:focus-within { opacity: 1; }
.bubble__tools:has([aria-pressed="true"]) { opacity: 1; }

.bubble__tool {
  width: 24px; height: 24px;
  display: grid; place-items: center;
  border: none; border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-mute);
  font-size: 12px;
  cursor: pointer;
}
.bubble__tool:hover { background: var(--color-gray-100); color: var(--color-text); }
.bubble__tool[aria-pressed="true"] { color: var(--color-primary); }

/* The note above the words it was heard as. */
.bubble__note { margin-bottom: var(--space-2); }

.note {
  align-self: center;
  font-size: var(--text-xs);
  color: var(--color-text-mute);
  font-style: italic;
}

/* ── The trail ────────────────────────────────────────────────────────────── */
.trail { margin-top: var(--space-3); font-size: var(--text-xs); }
.trail summary { cursor: pointer; color: var(--color-text-mute); }
.trail ul {
  list-style: none;
  margin-top: var(--space-2);
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: var(--color-text-soft);
}
.trail--failed { color: var(--color-danger); }

/* ── Working ──────────────────────────────────────────────────────────────── */
.working {
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius);
  background: var(--color-surface-2);
}
.working__text { font-size: var(--text-xs); color: var(--color-text-soft); }

.working__dots { display: inline-flex; gap: 4px; }
.working__dots i {
  width: 5px; height: 5px;
  border-radius: 50%;
  background: var(--color-text-mute);
  animation: typing 1.2s infinite;
}
.working__dots i:nth-child(2) { animation-delay: 0.2s; }
.working__dots i:nth-child(3) { animation-delay: 0.4s; }
@keyframes typing {
  0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
  30% { opacity: 1; transform: translateY(-3px); }
}

.live-steps {
  align-self: flex-start;
  list-style: none;
  font-size: var(--text-xs);
  color: var(--color-text-mute);
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-left: var(--space-2);
}

/* ── Confirmation ─────────────────────────────────────────────────────────── */
.confirm {
  padding: var(--space-3);
  border: 1px solid var(--color-warning);
  border-radius: var(--radius);
  background: var(--color-warning-soft);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.confirm__title { font-size: var(--text-xs); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
.confirm__what { font-size: var(--text-sm); font-weight: 600; }
.confirm__hint { font-size: var(--text-xs); color: var(--color-text-soft); }

.confirm__details { font-size: var(--text-xs); }
.confirm__details summary { cursor: pointer; color: var(--color-text-soft); }
.confirm__details pre {
  margin: var(--space-2) 0;
  padding: var(--space-2);
  background: var(--color-surface);
  border-radius: var(--radius-sm);
  font-family: var(--font-mono);
  overflow-x: auto;
}

.assistant__error {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: flex-start;
  padding: var(--space-3);
  background: var(--color-danger-soft);
  color: var(--color-danger);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
}

/* ── History ──────────────────────────────────────────────────────────────── */
.assistant__history { flex: 1; overflow-y: auto; padding: var(--space-2); }

.history-item {
  display: flex;
  align-items: center;
  border-radius: var(--radius-sm);
}
.history-item:hover { background: var(--color-gray-100); }
.history-item--current { background: var(--color-primary-soft); }

.history-item__open {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-3);
  border: none;
  background: none;
  text-align: left;
  font-size: var(--text-sm);
  color: var(--color-text);
}

.history-item__delete {
  margin-right: var(--space-2);
  opacity: 0;
}
.history-item:hover .history-item__delete,
.history-item__delete:focus-visible { opacity: 1; }

/* ── Composer ─────────────────────────────────────────────────────────────── */
/* The microphone sits with the send button, not above the box: it is another
   way to send, not another field.

   The ui-kit's recorder is built for a form, where «Grabar» next to the icon is
   the right amount of explaining. In a composer it is a third the width of the
   box. `font-size: 0` hides the word and keeps it for a screen reader, which is
   the one reading that still needs it. */
.composer__mic { flex: none; align-self: flex-end; display: flex; }
.composer__clip {
  flex: none;
  align-self: flex-end;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: none;
  color: var(--color-text-soft);
  cursor: pointer;
}
.composer__clip svg { width: 20px; height: 20px; fill: none; stroke: currentColor; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.composer__clip:hover, .composer__clip--on { color: var(--color-primary); }
.bubble__files { display: flex; flex-wrap: wrap; gap: var(--space-1); margin: 0 0 var(--space-1); padding: 0; list-style: none; }
.bubble__file { display: inline-flex; align-items: center; gap: var(--space-1); font-size: var(--text-xs); max-width: 100%; }
.bubble__file img { width: 48px; height: 48px; object-fit: cover; border-radius: var(--radius-sm); }
.bubble__file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 18ch; }
.composer__mic :deep(.rec) { display: flex; align-items: center; }
/* Redondo y del alto de la caja, como la flecha de enviar: el cuadro
   discontinuo del grabador es para un formulario, no para un chat. */
/* Flex y no grid: la palabra escondida (font-size 0) seguía siendo una
   segunda fila de la rejilla y subía el icono por encima del centro. */
.composer__mic :deep(.rec__start) {
  font-size: 0;
  line-height: 0;
  padding: 0;
  gap: 0;
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  border: none;
  border-radius: 50%;
  background: var(--color-surface-2);
  color: var(--color-text-soft);
}
.composer__mic :deep(.rec__start svg) { width: 20px; height: 20px; }
.composer__mic :deep(.rec__live) {
  gap: var(--space-1);
  font-size: var(--text-xs);
}
/* While recording the clock stays: it is the one number that stops somebody
   sending four minutes of van noise. */
.composer__mic :deep(.rec__stop) { font-size: 0; padding: 0 var(--space-2); }
.composer__mic :deep(.rec__problem) { display: none; }

/* La caja de una app de mensajes: una píldora que crece con el texto, con el
   micrófono y la flecha de enviar a los lados, redondos y del mismo alto. */
.composer {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-top: 1px solid var(--color-border);
  background: var(--color-surface);
  flex-shrink: 0;
}
.composer__input {
  flex: 1;
  min-width: 0;
  min-height: 40px;
  resize: none;
  padding: 9px var(--space-4);
  border: 1px solid var(--color-border);
  border-radius: 20px;
  background: var(--color-surface-2);
  color: var(--color-text);
  font-family: var(--font);
  font-size: var(--text-sm);
  line-height: 1.4;
  max-height: 160px;
}
/* La pista en un renglón: en un teléfono «Pregunta lo que quieras…» partía en
   dos dentro de una caja de uno y salía cortada por la mitad. */
.composer__input::placeholder {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.composer__input:focus {
  outline: none;
  border-color: var(--color-primary);
  background: var(--color-surface);
}

.composer__send {
  flex: none;
  width: 40px; height: 40px;
  display: grid;
  place-items: center;
  border: none;
  border-radius: 50%;
  background: var(--color-primary);
  color: var(--color-text-on-primary, #fff);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity var(--fast), transform var(--fast);
}
.composer__send svg {
  width: 20px; height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.composer__send:active:not(:disabled) { transform: scale(0.94); }
.composer__send:disabled { opacity: 0.35; cursor: default; }
.composer__send--stop { background: var(--color-surface-2); color: var(--color-text); border: 1px solid var(--color-border); }
.composer__send--stop svg { fill: currentColor; stroke: none; }

.assistant-slide-enter-active,
.assistant-slide-leave-active { transition: transform var(--base); }
.assistant-slide-enter-from,
.assistant-slide-leave-to { transform: translateX(100%); }

/* ── Phone ───────────────────────────────────────────────────────────────────
   Not a side panel any more, the screen: a 320px column beside a 70px sliver of
   the page underneath helps nobody. Last in the file on purpose, so it overrides
   the rules above rather than being overridden by them.

   The !important is for the inline width the resize grip writes — and the grip
   goes with it, since there is nothing left to resize against.
*/
@media (max-width: 640px) {
  /* Mide lo que queda por encima del teclado (ui-kit utils/viewport.js) y va
     con ello si iOS desplaza lo que se ve: la cabecera arriba y la caja justo
     encima de las teclas, nunca un hueco ni la cabecera fuera de la pantalla. */
  .assistant {
    width: 100% !important;
    top: var(--viewport-top, 0px);
    bottom: auto;
    height: var(--app-height, 100dvh);
    border-left: none;
    box-shadow: none;
    padding-top: env(safe-area-inset-top);
  }
  .grip { display: none; }

  /* El hilo no arrastra la página de detrás al llegar al final. */
  .assistant__thread { overscroll-behavior: contain; padding: var(--space-3) var(--space-2); }
  .bubble { font-size: var(--text-field); }
  .bubble--user { max-width: 85%; border-bottom-right-radius: 6px; }
  .bubble--assistant { padding: var(--space-3) var(--space-6) var(--space-3) var(--space-3); }

  /* Diana de dedo, como cualquier cabecera de app. */
  .head-btn { min-width: 44px; height: 44px; }
  /* En la cabecera estrecha, Llamar es solo el icono. */
  .head-btn__label { display: none; }
  .head-btn--call { padding: 0; width: 44px; }

  /* 16 px o iOS amplía la página al tocar la caja. */
  .composer__input { font-size: var(--text-field, 16px); }
  .composer__send { width: 44px; height: 44px; }
  .composer__clip { width: 44px; height: 44px; }
  .composer__mic :deep(.rec__start) { width: 44px; height: 44px; }
  /* El borde de abajo del teléfono, salvo con el teclado fuera: ahí no hay
     barra de inicio que esquivar y el margen sería un hueco. */
  .composer { padding-bottom: calc(var(--space-2) + env(safe-area-inset-bottom)); }
  /* Entero dentro de :global(): `:global(x) .y` en scoped se queda en `x`. */
  :global(html.keyboard-open .assistant .composer) { padding-bottom: var(--space-2); }
}

/* Con el dedo no hay hover: las herramientas de una respuesta (leer, copiar)
   se ven siempre, discretas, en vez de no existir. */
@media (hover: none) {
  .bubble__tools { opacity: 0.7; }
  /* Igual el borrar de cada conversación del historial: solo salía al pasar
     el ratón, así que en el teléfono no se podía borrar ninguna. */
  .history-item__delete { opacity: 1; }
}

.note--offline { margin: 0 var(--space-3); padding: 6px 10px; border-radius: var(--radius-sm); background: var(--color-warning-soft, var(--color-gray-100)); }
</style>
